import { _ } from "../polyglot/polyglot.js";
import { infoCircleIcon } from "../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ModalWidget from "../widgets/ModalWidget.js";

/**
 *
 * `TitleWidget` is used to render the page title, subtitle and the help.
 *
 * It builds a hidden modal window, so it requires a special disposal hook
 * to clean up the DOM.
 *
 * @param {Renderable} spec.title - Renderable object used to display the page title.
 * @param {Renderable} [spec.subtitle=''] - Optional renderable object used to render the page subtitle.
 * @param {Renderable} spec.helpContent - Renderable object used to display the content of the help modal.
 * @param {Renderable} [spec.id='document-title'] - CSS id of the main DIV element.
 * @param {Renderable} [spec.titleBuilder] - Object to which I will ask for the title and subtitle.
 * @example
 * titleWidget({
 *     title: 'Page Title',
 *     subtitle: 'Page subtitle',
 *     helpContent: helpContentWidget
 * })
 */
export default class TitleWidget extends Widget {
	constructor({
		title,
		subtitle = "",
		helpContent,
		id = "document-title",
		titleBuilder,
	}) {
		super(...arguments);
		this._title = title;
		this._subtitle = subtitle || "";
		this._helpContent = helpContent;
		this._titleId = id;
		this._titleBuilder = titleBuilder;

		this._contextHelpModal = new ModalWidget({
			title: this._title || _("Help"),
			content: (html) => html.render(this._helpContent),
		});
	}

	updateTitleAndSubtitle() {
		if (!this._titleBuilder) {
			this.update();
			return;
		}

		const newTitle = this._titleBuilder.getTitle();
		const newSubtitle = this._titleBuilder.getSubtitle();
		this._title = newTitle;
		this._subtitle = newSubtitle;
		this.update();
	}

	subtitle() {}

	renderContentOn(html) {
		html.div(
			{ id: this._titleId },
			html.h1(
				{
					"data-test": "document-title",
				},
				this._title,
				this._helpToggleRenderer(),
			),
			html.p(
				{ class: "subtitle", "data-test": "document-subtitle" },
				this._subtitle,
			),
		);
	}

	_helpToggleRenderer() {
		return (html) => {
			if (!this._helpContent) {
				return;
			}

			html.div(
				{ id: "info-toggler" },
				html
					.a({ "data-toggle": "modal" }, infoCircleIcon)
					.click(
						this._contextHelpModal.show.bind(
							this._contextHelpModal,
						),
					),
			);
		};
	}

	_willDetach() {
		this._contextHelpModal.willDetach();
	}
}
